import React from "react";
import AppLayout from "../components/AppLayout";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import {
  EmptyState,
  formatDate,
  formatMoney,
  getStatusClasses,
  SectionCard,
  SummaryTile,
  useTenantPortalData,
} from "./tenantPortalShared.jsx";

export default function TenantPaymentsPage({ isDark, setIsDark, currency, setCurrency }) {
  const { user } = useAuth();
  const toast = useToast();
  const { payments, paymentIntents, paymentSummary } = useTenantPortalData(user, toast);

  const paymentActivity = React.useMemo(() => {
    const paymentRecords = (payments || []).map((payment) => ({
      id: `payment-${payment.id}`,
      amount: payment.amount,
      invoiceId: payment.invoice_id || "",
      dueDate: payment.due_date || payment.payment_date || payment.created_at || "",
      status: payment.status || "Pending",
      method: payment.payment_method || "Not set",
      notes: payment.notes || "",
      reference: payment.provider_transaction_id || payment.reference || "",
      source: "payment",
      createdAt: payment.created_at || "",
    }));

    const existingPendingInvoiceIds = new Set(
      paymentRecords
        .filter((record) => String(record.status || "").toLowerCase() !== "paid")
        .map((record) => record.invoiceId)
        .filter(Boolean)
    );

    const pendingIntentRecords = (paymentIntents || [])
      .filter((intent) => {
        const normalizedStatus = String(intent.status || "").toLowerCase();
        return ["pending", "submitted"].includes(normalizedStatus) && !existingPendingInvoiceIds.has(intent.invoice_id);
      })
      .map((intent) => ({
        id: `intent-${intent.id}`,
        amount: intent.amount,
        invoiceId: intent.invoice_id || "",
        dueDate: intent.created_at || "",
        status: "Pending",
        method: "M-Pesa request",
        notes: "Payment request created and awaiting collection.",
        reference: intent.external_reference || "",
        source: "intent",
        createdAt: intent.created_at || "",
      }));

    return [...pendingIntentRecords, ...paymentRecords].sort((a, b) => {
      const left = new Date(a.createdAt || a.dueDate || 0).getTime();
      const right = new Date(b.createdAt || b.dueDate || 0).getTime();
      return right - left;
    });
  }, [payments, paymentIntents]);

  return (
    <AppLayout
      isDark={isDark}
      setIsDark={setIsDark}
      currency={currency}
      setCurrency={setCurrency}
      eyebrow="Resident Portal"
      title="My payments"
      description="Recent rent payment records linked to your tenancy."
    >
      <SectionCard
        title="Payment summary"
        description="A quick view of paid and pending payment amounts."
        isDark={isDark}
      >
        <div className="grid gap-3 md:grid-cols-3">
          <SummaryTile label="Records" value={paymentSummary.total} isDark={isDark} />
          <SummaryTile label="Paid" value={formatMoney(paymentSummary.paid, currency)} isDark={isDark} />
          <SummaryTile label="Pending" value={formatMoney(paymentSummary.pending, currency)} isDark={isDark} />
        </div>
      </SectionCard>

      <SectionCard
        title="Payment history"
        description="Payments recorded against your resident account."
        isDark={isDark}
      >
        {paymentActivity.length === 0 ? (
          <EmptyState
            title="No payment history yet"
            body="When rent payments are linked to this tenancy, they will appear here."
            isDark={isDark}
          />
        ) : (
          <div className="space-y-3">
            {paymentActivity.map((payment) => (
              <div
                key={payment.id}
                className={`rounded-2xl border p-4 ${
                  isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"
                }`}
              >
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div>
                    <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                      {formatMoney(payment.amount, currency)}
                    </p>
                    <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                      {payment.source === "intent" ? "Requested" : "Due"} {formatDate(payment.dueDate)}
                      {payment.invoiceId ? ` | Invoice ${payment.invoiceId}` : ""}
                    </p>
                  </div>
                  <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${getStatusClasses(payment.status)}`}>
                    {payment.status}
                  </span>
                </div>
                <div className={`mt-3 flex flex-wrap gap-4 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  <span>Method: {payment.method || "Not set"}</span>
                  {payment.reference ? <span>Reference: {payment.reference}</span> : null}
                  {payment.notes ? <span>Note: {payment.notes}</span> : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </AppLayout>
  );
}
